import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiFolder,
  FiFile,
  FiChevronRight,
  FiX,
  FiCheck
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const DriveBrowserModal = ({ isOpen, onClose, onSelect, googleDriveService }) => {
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: 'root', name: 'My Drive' }]);
  const [error, setError] = useState(null);

  const loadFolder = useCallback(async (folderId, folderName = null) => {
    setLoading(true);
    setError(null);
    try {
      const contents = await googleDriveService.listFolderContents(folderId);
      setItems(contents);
      setCurrentFolderId(folderId);

      if (folderName) {
        setBreadcrumbs(prev => {
           // check if we are going back
           const index = prev.findIndex(b => b.id === folderId);
           if (index >= 0) {
               return prev.slice(0, index + 1);
           }
           return [...prev, { id: folderId, name: folderName }];
        });
      }
    } catch (err) {
      console.error('Failed to load folder:', err);
      // If we fail to load root or initial folder, show error clearly
      const message = err.message || 'Failed to load folder contents';
      setError(message);
      toast.error('Failed to load Google Drive folder');
    } finally {
      setLoading(false);
    }
  }, [googleDriveService]);

  useEffect(() => {
    if (isOpen) {
      loadFolder('root');
    }
  }, [isOpen, loadFolder]);

  const handleNavigate = (item) => {
    if (item.type === 'folder') {
      loadFolder(item.id, item.name);
    }
  };

  const handleFormattedBreadcrumbClick = (crumb) => {
      // If we click a breadcrumb, we want to reload that folder
      // logic in loadFolder handles breadcrumb slicing if we pass name,
      // but here we can just manually slice state and call with ID
      const index = breadcrumbs.findIndex(b => b.id === crumb.id);
      if (index >= 0) {
          const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
          setBreadcrumbs(newBreadcrumbs);
          loadFolder(crumb.id); // don't pass name so it doesn't mess up slicing further
      }
  };

  const currentFolder = breadcrumbs[breadcrumbs.length - 1];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex flex-col w-full max-w-4xl h-[80vh] bg-base-100 rounded-xl shadow-2xl overflow-hidden border border-base-content/10"
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-base-200/50">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">Select Google Drive Folder</h2>
                </div>
                <button onClick={onClose} className="btn btn-sm btn-ghost btn-circle">
                    <FiX className="w-5 h-5" />
                </button>
            </div>

            {/* Breadcrumbs */}
            <div className="px-4 py-3 border-b bg-base-100 text-sm flex items-center gap-1 overflow-x-auto whitespace-nowrap">
                {breadcrumbs.map((crumb, i) => (
                    <div key={crumb.id} className="flex items-center">
                        {i > 0 && <FiChevronRight className="mx-1 text-base-content/40" />}
                        <button
                            className={`hover:bg-base-200 px-2 py-1 rounded transition-colors ${i === breadcrumbs.length - 1 ? 'font-semibold text-primary' : 'text-base-content/70'}`}
                            onClick={() => handleFormattedBreadcrumbClick(crumb)}
                        >
                            {crumb.name}
                        </button>
                    </div>
                ))}
            </div>

            {/* Content Listing */}
            <div className="flex-1 overflow-y-auto p-4 bg-base-100">
                {error ? (
                    <div className="flex flex-col items-center justify-center h-full text-error p-8 text-center">
                        <div className="text-lg font-semibold mb-2">Error Loading Folder</div>
                        <p>{error}</p>
                        <button className="btn btn-outline btn-sm mt-4" onClick={() => loadFolder(currentFolderId)}>Retry</button>
                    </div>
                ) : loading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                        <span className="loading loading-spinner loading-lg text-primary"></span>
                        <p className="text-base-content/60">Loading contents...</p>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-base-content/40">
                        <FiFolder className="w-16 h-16 mb-4 opacity-20" />
                        <p>This folder is empty</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => handleNavigate(item)}
                                className={`
                                    flex items-center gap-3 p-3 rounded-lg border transition-all duration-200
                                    ${item.type === 'folder' 
                                        ? 'cursor-pointer hover:bg-primary/5 hover:border-primary/30 border-base-200 bg-base-50' 
                                        : 'opacity-60 cursor-default border-transparent bg-transparent'
                                    }
                                `}
                            >
                                <div className={`p-2 rounded-lg ${item.type === 'folder' ? 'bg-primary/10 text-primary' : 'bg-base-200 text-base-content/50'}`}>
                                    {item.type === 'folder' ? <FiFolder className="w-5 h-5" /> : <FiFile className="w-5 h-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate text-sm" title={item.name}>
                                        {item.name}
                                    </div>
                                    <div className="text-xs text-base-content/50 flex gap-2">
                                        {item.size ? (Number(item.size) / 1024 / 1024).toFixed(1) + ' MB' : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-base-200/50 flex items-center justify-between">
                <div className="text-sm text-base-content/60">
                    Selected: <span className="font-semibold text-base-content">{currentFolder?.name}</span>
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={() => {
                            if (currentFolder) {
                                onSelect({ id: currentFolder.id, name: currentFolder.name });
                                onClose();
                            }
                        }}
                        className="btn btn-primary gap-2"
                        disabled={loading || !currentFolder}
                    >
                        <FiCheck className="w-4 h-4" />
                        Select This Folder
                    </button>
                </div>
            </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DriveBrowserModal;

